import { CSSProperties, KeyboardEvent, useRef } from 'react';
import type { ChoiceOption } from './ChoiceChips';

/**
 * ChoiceList — single-choice radio rows on hairlines, for answers that read
 * as sentences (e.g. how much walking is comfortable). Picking a row is the
 * answer; there's no Continue.
 *
 * role="radiogroup" with roving focus: arrow keys move between rows,
 * Enter/Space picks — arrows never pick, because picking advances the flow.
 */
export interface ChoiceListProps {
  options: ChoiceOption[];
  value: string | null;
  onChange: (id: string) => void;
  /** Accessible name for the group (usually the sheet title). */
  label: string;
  style?: CSSProperties;
}

export function ChoiceList({ options, value, onChange, label, style }: ChoiceListProps) {
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const focusIndex = Math.max(0, options.findIndex((o) => o.id === value));

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    rowRefs.current[(index + step + options.length) % options.length]?.focus();
  }

  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexDirection: 'column', ...style }}>
      {options.map((option, i) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            ref={(el) => { rowRefs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={i === focusIndex ? 0 : -1}
            onClick={() => onChange(option.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className="sk-flow-focus"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minHeight: 48,
              padding: '12px 2px',
              border: 'none',
              borderTop: '1px solid var(--line)',
              borderBottom: i === options.length - 1 ? '1px solid var(--line)' : 'none',
              background: 'transparent',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 400,
              lineHeight: 1.35,
              textAlign: 'left',
              color: selected ? 'var(--text)' : 'var(--chip-text)',
              cursor: 'pointer',
              transition: 'color .15s',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                flex: 'none',
                width: 18,
                height: 18,
                boxSizing: 'border-box',
                borderRadius: '50%',
                border: selected
                  ? '5px solid var(--accent)'
                  : '1.5px solid color-mix(in srgb, var(--text) 35%, transparent)',
                transition: 'border .15s',
              }}
            />
            <span style={{ minWidth: 0 }}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default ChoiceList;
